<template>
  <res-dialog :showDialog="showDialog"
              title="Import Budget"
              :titleClass="'resBlue darken-1 white--text'"
              :iconColor="'white'"
              :maxWidth="maxWidth"
              :setWidth="width"
              saveButtonText="NEXT"
              :saveDisabled="isSaveDisabled"
              cancelButtonText="Cancel"
              :setHeight="height"
              @cancel="cancel"
              @save="save">
    <res-dialog :showDialog="showHelp"
                titleClass="resBlue darken-1 white--text"
                iconColor="white"
                cancelButtonText="Close"
                :cancelButtonOnly="true"
                cancelButtonColor="resBlue darken-1"
                :maxWidth="800"
                :setWidth="800"
                :setHeight="600"
                @cancel="closeHelp">
      <import-budget-help :openSection="openHelpSection"></import-budget-help>
    </res-dialog>
    <v-container>
      <v-row>
        <v-col cols="10" sm="10" class="res-col-10">
          <span class="font-weight-medium">Please select from the following options to import your property budget for a given year.</span>
        </v-col>
        <v-col cols="2" sm="2" class="res-col-10">
          <div class="help-controls" @click="openHelp(null)">
            <span class="help-label">Help</span>
            <v-icon color="var(--v-resBlue-darken1)" class="pb-1" small>mdi-help-circle</v-icon>
          </div>
        </v-col>
      </v-row>
      <v-row class="ma-0">
        <v-col cols="10" sm="10" class="res-col-10">
          <span class="font-weight-medium">To ensure a sucessful import, the imported budget file must be formatted as follows:</span><br />
          <ul>
            <li>in a comma separated value (csv) format</li>
            <li>first column should reflect GL Account Number</li>
            <li>second column should reflect GL Account Name</li>
            <li>third column should reflect 1st month of your fiscal year</li>
            <li>fourth column should reflect 2nd month of your fiscal year</li>
            <li>each additional column should reflect each subsequent month of your fiscal year</li>
          </ul>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="4" sm="4" class="res-col-10">
          <div class="d-flex flex-column flex-grow-1">
            <label class="required">Property</label>
            <property-selector v-model="budgetToImport.property" elementId="import-property-selector"></property-selector>
          </div>
        </v-col>
        <v-col cols="3" sm="3" class="res-col-10">
          <label id="start-month-label" class="required">Starting Month</label>
          <res-select id="import-start-month-selector" class="" v-model="budgetToImport.startMonth" :items="startMonthOptions" attach="#import-start-month-selector"></res-select>
        </v-col>
        <v-col cols="2" sm="2" class="res-col-10">
          <label class="required">Year</label>
          <fiscal-year-selector v-model="budgetToImport.year" :propertyId="budgetToImport.property.value" elementId="import-year-selector"></fiscal-year-selector>
        </v-col>
        <v-col cols="3" sm="3" class="res-col-10">
          <label class="required">Accounting Basis</label><br />
          <v-btn-toggle v-model="budgetToImport.accountingBasisId" mandatory dense rounded :active-class="'resBlue darken-1 white--text'">
            <v-btn width="80" height="30" min-height="30" outlined value="1">
              ACCRUAL
            </v-btn>
            <v-btn width="80" height="30" min-height="30" outlined value="2">
              CASH
            </v-btn>
          </v-btn-toggle>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <label class="required">Import file</label><br />
          <v-file-input label="CHOOSE FILE"
                        v-model="budgetToImport.file"
                        outlined
                        dense
                        single-line
                        show-size
                        truncate-length="200"
                        prepend-icon="mdi-paperclip"
                        accept=".csv">
            <template v-slot:selection="{ text }">
              <v-chip small
                      label
                      color="primary">
                {{ text }}
              </v-chip>
            </template>
          </v-file-input>
        </v-col>
      </v-row>
    </v-container>
  </res-dialog>
</template>

<script>
  import ResDialog from '@/components/base/ResDialog.vue';
  import ResSelect from '@/components/base/ResSelect.vue';
  import PropertySelector from '@/components/properties/PropertySelector.vue';
  import FiscalYearSelector from '@/components/properties/FiscalYearSelector.vue';
  import IsNullOrWhitespaceMixin from '@/mixins/isNullOrWhitespace.js';

  import HelpableMixin from '@/mixins/Helpable.js';
  import ImportBudgetHelp from '@/components/help/ImportBudgetHelp.vue';
  import propertyService from '@/services/property';
  export default {    
    props: {
      height: {
        type: Number
      },
      maxWidth: {
        type: Number
      },
      showDialog: {
        type: Boolean
      },
      width: {
        type: Number
      }
    },
    mixins: [IsNullOrWhitespaceMixin, HelpableMixin ],
    data() {
      return {
        budgetToImport: {
          property: { text: '', value: '' },
          startMonth: null,
          year: null,
          accountingBasisId: 1, // Accrual
          file: null,
          budgetIterationId: null,
          budgetSettings: {}
        }
      }
    },
    components: {    
      ResDialog,
      ResSelect,
      PropertySelector,
      FiscalYearSelector,
      ImportBudgetHelp
    },
    methods: {
      save () {     
        this.$emit('next', this.budgetToImport)        
      },
      cancel () {
        this.$emit('cancel', true)
      },
      async selectDefaultMonth(propertyId) {
        this.budgetToImport.startMonth = await propertyService.getPropertyFiscalYearStartMonth(propertyId);
      },
      getMonthOptions(year, includeYear, startMonth) {
        startMonth = startMonth || 0;
        let priorYear = includeYear ? year - 1 : '';
        if (priorYear > 0 || !includeYear) {
          var months = [
            { text: 'January ', value: 1 },
            { text: 'February ', value: 2 },
            { text: 'March ', value: 3 },
            { text: 'April ', value: 4 },
            { text: 'May ', value: 5 },
            { text: 'June ', value: 6 },
            { text: 'July ', value: 7 },
            { text: 'August ', value: 8 },
            { text: 'September ', value: 9 },
            { text: 'October ', value: 10 },
            { text: 'November ', value: 11 },
            { text: 'December ', value: 12 }
          ];

          var returnMonths = [];
          for (var i = startMonth; i < startMonth + 12; i++) {
            var thisMonth = months[i % 12];
            thisMonth.text += i < 12 && i > 0 ? priorYear : i < 24 ? year : year + 1;
            returnMonths.push(thisMonth)
          }
          return returnMonths;
        }
        return []
      },
    },
    computed: {
      startMonthOptions() {
        return this.getMonthOptions(this.budgetToImport.year, true);
      },
      propertyIsSelected() {
        return this.budgetToImport.property && this.budgetToImport.property.value && this.budgetToImport.property.value.length == 36; /* guid length */
      },
      isSaveDisabled() {
        // make sure we have selected everything        
        if (this.propertyIsSelected == false || this.isNullOrWhitespace(this.budgetToImport.startMonth) || this.isNullOrWhitespace(this.budgetToImport.year) || this.isNullOrWhitespace(this.budgetToImport.accountingBasisId) || this.isNullOrWhitespace(this.budgetToImport.file)) {
          return true;
        }
        return false;
      }
    },
    watch: {
      'budgetToImport.property.value': function (newVal) {
        if (newVal) {
          this.selectDefaultMonth(newVal);         
        }
      }
    }
  };
</script>

<style lang="scss" scoped>
  .base-dialog {
    .res-col-10 {
      padding-top: 10px !important;
      padding-bottom: 10px !important;
    }
    .v-btn-toggle--rounded {
      border-radius: 6px;
      border-top-left-radius: 6px;
      border-top-right-radius: 6px;
      border-bottom-right-radius: 6px;
      border-bottom-left-radius: 6px;
    }
  }
  .theme--light.v-btn-toggle::v-deep {
    .v-btn.v-btn {
      border: solid 1px var(--v-resGrey-darken1) !important;
    }
  }
  .v-input.v-input--dense.v-input--selection-controls.v-input--radio-group.v-input--radio-group--row::v-deep {
    .v-input--selection-controls__input {
      height: 20px !important;
      width: 20px !important;
    }
    .v-input--selection-controls__ripple {
      width: 20px;
      height: 20px;
      left: -10px;
    }
    .v-icon--svg {
      height: 15px !important;
      width: 15px !important;
    }
  }


  .v-input#import-property-selector::v-deep {
    div.menuable__content__active {
    max-height: 118px !important;
  }
}

  .v-input#import-start-month-selector::v-deep {
    div.menuable__content__active {
      max-height: 118px !important;
    }
  } 

  .v-input#import-year-selector::v-deep {
    div.menuable__content__active {
    max-height: 118px !important;
  }
  } 
  /*.v-file-input::v-deep {*/
 /*   width: 150px;
    height: 30px;
    label {
    font-size: 12px;
  }*/

    /*.v-input__prepend-outer
  {
    margin: 0;
    width: 0;
    height: 0;
  }
&.v-input--is-label-active {
    width: initial;
}
  }*/


  .help-controls {
    height: 100%;
    float: right;
    border: solid 1px var(--v-resGrey-lighten3);
    padding: 7px 6px 6px 6px;
  }

  .help-label {
    padding-right: 6px;
  }
  .v-alert::v-deep{
      .v-alert__wrapper
  {
    color: #4f4f4f;
    font-size: 12px;
    .v-icon
  {
    color: #4f4f4f;
  }
  }
  }
</style>
